import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";

import { useServices } from "@/hooks/useServices";
import { ArrowUpRight } from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";
import SliderNav from "@/components/SliderNav";
import ContactSheet from "@/components/ContactSheet";
import framerCommerceLogo from "@/assets/tools/framer-commerce.png";
import DallasLocalsCTA from "@/components/DallasLocalsCTA";

/** Inverted-dot custom cursor link for Framer Commerce logo */
const FramerCommerceLink = () => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const [hovering, setHovering] = useState(false);

  const onMove = useCallback((e: React.MouseEvent) => {
    if (!linkRef.current || !dotRef.current) return;
    const rect = linkRef.current.getBoundingClientRect();
    dotRef.current.style.left = `${e.clientX - rect.left}px`;
    dotRef.current.style.top = `${e.clientY - rect.top}px`;
  }, []);

  return (
    <a
      ref={linkRef}
      href="https://framercommerce.com"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Visit Framer Commerce"
      className="relative inline-flex items-center"
      style={{ cursor: "none" }}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onMouseMove={onMove}
    >
      <div
        ref={dotRef}
        className="pointer-events-none absolute rounded-full"
        style={{
          width: 20,
          height: 20,
          backgroundColor: "white",
          mixBlendMode: "difference",
          opacity: hovering ? 1 : 0,
          transform: `translate(-50%, -50%) scale(${hovering ? 1 : 0.3})`,
          transition: "opacity 0.15s ease-out, transform 0.15s ease-out",
        }}
      />
      <img
        src={framerCommerceLogo}
        alt="Framer Commerce"
        className="h-3.5 dark:invert"
      />
    </a>
  );
};

const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [activeIndex, setActiveIndex] = useState(0);
  const { mode } = useTheme();
  const { services } = useServices();

  // Preload only the active service image (not all at once)
  useEffect(() => {
    const s = services[activeIndex];
    if (!s) return;
    const img = new Image();
    img.src = s.image;
    if (s.imageDark) {
      const imgDark = new Image();
      imgDark.src = s.imageDark;
    }
  }, [activeIndex, services]);

  const current = services[activeIndex];

  const goTo = (i: number) => {
    if (i >= 0 && i < services.length) setActiveIndex(i);
  };

  return (
    <section id="services" ref={ref} className="section-light py-16 md:py-24 px-5 md:px-10 lg:px-14">
      <div>
      {/* Two-column layout */}
      <div className="flex flex-col lg:flex-row lg:items-stretch lg:gap-0 min-h-[50vh] lg:min-h-[70vh]">
        {/* Left: Content */}
        <div className="flex-1 flex flex-col justify-between pr-0 lg:pr-12 xl:pr-20">
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p className="font-body text-[9px] font-medium tracking-[0.25em] uppercase text-muted-foreground mb-2">
              03 — Our Services
            </p>
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="flex-1 flex flex-col justify-center"
            >
              <h2 className="font-display italic text-4xl md:text-[48px] lg:text-[56px] leading-[1.1] text-foreground mb-8 max-w-md">
                {current.title}
              </h2>

              <p className="font-body text-[13px] md:text-[14px] text-muted-foreground leading-[1.8] max-w-lg mb-8 lg:mb-12">
                {current.title === "Framer E-Commerce Development" ? (
                  <>
                    E-comm solutions built in{" "}
                    <a href="https://www.framer.com/" target="_blank" rel="noopener noreferrer" className="link-dotted">Framer</a>
                    {" "}utilizing{" "}
                    <a href="https://framercommerce.com/" target="_blank" rel="noopener noreferrer" className="link-dotted">Framer Commerce</a>
                    {" "}and{" "}
                    <a href="https://frameship.io/" target="_blank" rel="noopener noreferrer" className="link-dotted">Frameship</a>
                    {" "}to create a custom frontend storefront experience while Shopify runs the show on the backend. We focus on store logic — product structure, collection hierarchy, conditional displays, and CMS alignment. The goal is a storefront that feels editorial but functions predictably behind the scenes.
                  </>
                ) : current.title.includes("Klaviyo") ? (
                  <>
                    Email should not feel like a separate department from your website. We look at how people move through your site first — what they click, where they hesitate, what they abandon. Then we structure flows around that reality. Inside{" "}
                    <a href="https://www.klaviyo.com/" target="_blank" rel="noopener noreferrer" className="link-dotted">Klaviyo</a>
                    , we clean up what's messy, build modular templates your team can reuse, and set up flows that make sense.
                  </>
                ) : (
                  current.description
                )}
              </p>

              {current.details && current.details.length > 0 && (
                <div className="relative">
                  <span aria-hidden="true" className="font-display italic text-[clamp(2rem,5vw,3.5rem)] text-foreground/[0.04] dark:text-foreground/[0.08] absolute -top-8 left-0 select-none pointer-events-none leading-none">
                    TL;DR
                  </span>
                  <div className="grid grid-cols-2 gap-x-10 gap-y-2.5 mb-10 max-w-lg relative">
                  {current.details.map((detail) => (
                    <span
                      key={detail}
                      className="font-body text-[12px] text-foreground/70 flex items-center gap-2"
                    >
                      <span className="w-1 h-1 rounded-full bg-foreground/40 flex-shrink-0" />
                      {detail === "Synced inventory management" && (
                        <FramerCommerceLink />
                      )}
                      {detail}
                    </span>
                  ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4">
                <ContactSheet defaultProjectType={current.title}>
                  <button className="inline-flex items-center gap-1.5 px-5 py-2.5 border border-foreground text-foreground font-body text-[9px] font-medium tracking-[0.15em] uppercase hover:bg-foreground hover:text-background transition-colors">
                    Let's talk
                    <ArrowUpRight size={10} />
                  </button>
                </ContactSheet>
                <span className="font-body text-[12px] text-muted-foreground">
                  {current.pricing}
                </span>
              </div>

              {/* Cross-links between Web Design & Landing Page */}
              {current.title.includes("Design & Development") && (() => {
                const lpIndex = services.findIndex((s) => s.title.includes("Landing Page"));
                return lpIndex !== -1 ? (
                  <p className="mt-6 font-body text-[10px] tracking-[0.05em] text-muted-foreground/70 italic">
                    Need something lighter?{" "}
                    <button
                      onClick={() => goTo(lpIndex)}
                      className="not-italic underline decoration-dotted underline-offset-[3px] hover:text-muted-foreground transition-colors"
                    >
                      Try a landing page
                    </button>
                  </p>
                ) : null;
              })()}
              {current.title.includes("Landing Page") && (() => {
                const ddIndex = services.findIndex((s) => s.title.includes("Design & Development"));
                return ddIndex !== -1 ? (
                  <p className="mt-6 font-body text-[10px] tracking-[0.05em] text-muted-foreground/70 italic">
                    Need more weight?{" "}
                    <button
                      onClick={() => goTo(ddIndex)}
                      className="not-italic underline decoration-dotted underline-offset-[3px] hover:text-muted-foreground transition-colors"
                    >
                      Try full-scale design & dev
                    </button>
                  </p>
                ) : null;
              })()}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="mt-8 lg:mt-14">
            <SliderNav
              activeIndex={activeIndex}
              total={services.length}
              onNavigate={goTo}
              label="service"
            />
          </div>
        </div>

        {/* Right: Only render active + adjacent images for lazy loading */}
        <div className="flex-1 flex items-center justify-center mt-8 lg:mt-0">
          <div className="w-full max-w-[520px] lg:max-w-[640px] xl:max-w-[720px] relative aspect-[4/3]">
            <AnimatePresence mode="wait">
              {(() => {
                const s = services[activeIndex];
                if (!s) return null;
                const src = mode === "dark" && s.imageDark ? s.imageDark : s.image;
                return (
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="absolute inset-0"
                  >
                    <img
                      src={src}
                      alt={s.title}
                      className="w-full h-full object-contain"
                    />
                  </motion.div>
                );
              })()}
            </AnimatePresence>
          </div>
        </div>
      </div>
      </div>


      {/* Dallas Locals CTA nested below carousel */}
      <div id="dallas-locals" className="mt-12 md:mt-16 lg:mt-24 pt-10 md:pt-16">
        <DallasLocalsCTA />
      </div>
    </section>
  );
};

export default ServicesSection;
